import { queryKeys, useHttpClient, useActiveOrganizationId } from '@repo/api-client/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PaymentSchema,
  PaymentSummarySchema,
  PaymentsListResponseSchema,
  type CreatePaymentRequest,
  type Payment,
  type UpdatePaymentRequest,
} from '@repo/api-client/dtos';
import { useMemo } from 'react';
import { z } from 'zod';
import { deleteJSON, getJSON, patchJSON, postJSON } from '../internal/http';

const OkSchema = z.object({ ok: z.boolean() }).passthrough();

/**
 * Payments recorded against a single invoice. Cursor-paginated via
 * `useInfiniteQuery` so the invoice detail screen's payments
 * section can lazy-load past page-1 — the page is mostly small but
 * a long-running customer can accrue dozens of partial payments.
 */
export const PAYMENTS_PAGE_SIZE = 50;

export function useInvoicePayments(invoiceId: string) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();

  const {
    error,
    isPending: loading,
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey:
      orgId && invoiceId
        ? queryKeys.payments.byInvoice(orgId, invoiceId)
        : ['payments', 'idle'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: String(PAYMENTS_PAGE_SIZE) });
      if (pageParam) search.set('cursor', pageParam);
      return getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/payments?${search.toString()}`,
        PaymentsListResponseSchema,
      );
    },
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!orgId && !!invoiceId,
  });

  const payments = useMemo<Payment[] | undefined>(
    () =>
      data
        ? (data.pages.flatMap((page) => page.items) as Payment[])
        : undefined,
    [data],
  );

  return {
    payments,
    nextCursor: data?.pages.at(-1)?.nextCursor,
    loading,
    error,
    refetch,
    hasNextPage,
    fetchNextPage: () => fetchNextPage(),
    isFetchingNextPage,
  };
}

/**
 * Aggregated paid / refunded summary computed by the server. Mirrors
 * what the dashboard's "balance" widget needs without having to walk
 * the full payments list.
 */
export function usePaymentSummary(invoiceId: string) {
  const http = useHttpClient();
  const orgId = useActiveOrganizationId();

  return useQuery({
    queryKey:
      orgId && invoiceId
        ? queryKeys.payments.summary(orgId, invoiceId)
        : ['payments', 'idle'],
    queryFn: () =>
      getJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/payments/summary`,
        PaymentSummarySchema,
      ),
    enabled: !!orgId && !!invoiceId,
  });
}

export interface CreatePaymentInput {
  invoiceId: string;
  body: CreatePaymentRequest;
}

/**
 * Record a payment (or refund) against an invoice. The server
 * recomputes the invoice's `amountPaid`/`amountDue` totals and writes
 * an activity row, so we invalidate the matching caches.
 */
export function useCreatePayment() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  return useMutation({
    mutationFn: ({ invoiceId, body }: CreatePaymentInput) => {
      if (!orgId) throw new Error('No organization selected');
      return postJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/payments`,
        body,
        PaymentSchema,
      );
    },
    onSuccess: (payment) => {
      if (!orgId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.byInvoice(orgId, payment.invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.summary(orgId, payment.invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.detail(orgId, payment.invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.activities(orgId, payment.invoiceId),
      });
    },
  });
}

export function useUpdatePayment() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  return useMutation({
    mutationFn: ({
      invoiceId,
      paymentId,
      updates,
    }: {
      invoiceId: string;
      paymentId: string;
      updates: UpdatePaymentRequest;
    }) => {
      if (!orgId) throw new Error('No organization selected');
      return patchJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/payments/${paymentId}`,
        updates,
        PaymentSchema,
      );
    },
    onSuccess: (payment) => {
      if (!orgId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.byInvoice(orgId, payment.invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.summary(orgId, payment.invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.detail(orgId, payment.invoiceId),
      });
    },
  });
}

export function useDeletePayment() {
  const http = useHttpClient();
  const queryClient = useQueryClient();
  const orgId = useActiveOrganizationId();

  return useMutation({
    mutationFn: async ({ invoiceId, paymentId }: { invoiceId: string; paymentId: string }) => {
      if (!orgId) throw new Error('No organization selected');
      await deleteJSON(
        http,
        `/orgs/${orgId}/invoices/${invoiceId}/payments/${paymentId}`,
        OkSchema,
      );
      return { invoiceId, paymentId };
    },
    onSuccess: ({ invoiceId }) => {
      if (!orgId) return;
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.byInvoice(orgId, invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.payments.summary(orgId, invoiceId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.invoices.detail(orgId, invoiceId),
      });
    },
  });
}

/**
 * Pure aggregation helper used by some screens. Lives here only as a
 * convenience — the server's `usePaymentSummary` is the canonical
 * source of truth, and screens should prefer it.
 *
 * NOTE: amounts are decimal strings in minor units. Uses `BigInt`
 * arithmetic so we don't lose precision on multi-hundred-thousand-
 * dollar invoices.
 */
export function summarisePayments(payments: Payment[] = []) {
  let totalPaid = 0n;
  let totalRefunded = 0n;
  let totalFees = 0n;
  for (const p of payments) {
    if (p.status !== 'completed') continue;
    const amt = BigInt(p.amount);
    if (p.type === 'payment') totalPaid += amt;
    else if (p.type === 'refund') totalRefunded += amt < 0n ? -amt : amt;
    else if (p.type === 'fee') totalFees += amt;
  }
  return {
    totalPaid: totalPaid.toString(),
    totalRefunded: totalRefunded.toString(),
    totalFees: totalFees.toString(),
    netPaid: (totalPaid - totalRefunded).toString(),
  };
}

export function formatPaymentMethod(method: string): string {
  const methodMap: Record<string, string> = {
    bank_transfer: 'Bank Transfer',
    credit_card: 'Credit Card',
    debit_card: 'Debit Card',
    cash: 'Cash',
    check: 'Check',
    paypal: 'PayPal',
    stripe: 'Stripe',
    wire_transfer: 'Wire Transfer',
    online_banking: 'Online Banking',
    mobile_payment: 'Mobile Payment',
    cryptocurrency: 'Cryptocurrency',
    other: 'Other',
  };
  return methodMap[method] ?? method;
}

export function getPaymentMethodIcon(method: string): string {
  const iconMap: Record<string, string> = {
    bank_transfer: 'business-outline',
    credit_card: 'card-outline',
    debit_card: 'card-outline',
    cash: 'cash-outline',
    check: 'receipt-outline',
    paypal: 'logo-paypal',
    stripe: 'card-outline',
    wire_transfer: 'swap-horizontal-outline',
    online_banking: 'globe-outline',
    mobile_payment: 'phone-portrait-outline',
    cryptocurrency: 'logo-bitcoin',
    other: 'ellipsis-horizontal-outline',
  };
  return iconMap[method] ?? 'card-outline';
}
